import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

/**
 * This does not seem to work on mobile devices because of keydown / keyup handling
 * https://codeburst.io/digit-only-directive-in-angular-3db8a94d80c3
 * https://clark.engineering/input-on-android-229-unidentified-1d92105b9a04
 * https://stackoverflow.com/questions/30743490/capture-keys-typed-on-android-virtual-keyboard-using-javascript
 * https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
 * https://www.outsystems.com/blog/javascript-events-unmasked-how-to-create-input-mask-for-mobile.html
 *
 * @export
 * @class DigitOnlyDirective
 * @implements {OnChanges}
 */
@Directive({
  selector: '[gsDigitOnly]',
  standalone: false
})
export class DigitOnlyDirective implements OnChanges {
  private hasDecimalPoint = false;
  private isInputSupported = false;
  private navigationKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowLeft',
    'ArrowRight',
    'Clear',
    'Copy',
    'Paste',
  ];

  @Input() decimal = false;
  @Input() decimalSeparator = '.';
  @Input() min = -Infinity;
  @Input() max = Infinity;
  @Input() numberPattern?: string | RegExp;
  private regex: RegExp;
  inputElement: HTMLInputElement;

  constructor(
    private el: ElementRef,
    @Inject(DOCUMENT) private document: Document

  ) {
    this.inputElement = el.nativeElement;
    this.isInputSupported = this.checkInputSupported();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.numberPattern) {
      this.regex = this.numberPattern ? RegExp(this.numberPattern) : null;
    }

    if (changes.min) {
      const maybeMin = Number(this.min);
      this.min = isNaN(maybeMin) ? -Infinity : maybeMin;
    }

    if (changes.max) {
      const maybeMax = Number(this.max);
      this.max = isNaN(maybeMax) ? Infinity : maybeMax;
    }
  }

  //  //Returns 229 on android chrome
  // @HostListener('keydown', ['$event'])
  // onkeydown(e: KeyboardEvent): any {
  //   console.log(`keydown ${e.key} - ${e.keyCode} - ${e.char} - ${e.charCode} - ${e.code} - ${e.composed} - ${e.detail} - ${e.which} - ${e.returnValue}`);
  // }

  //  //Returns 229 on android chrome
  // @HostListener('keyup', ['$event'])
  // onkeyup(e: KeyboardEvent): any {
  //   console.log(`keyup ${e.key} - ${e.keyCode} - ${e.char} - ${e.charCode} - ${e.code} - ${e.composed} - ${e.detail} - ${e.which} - ${e.returnValue}`);
  // }

  // //This is not fired on android chrome
  // @HostListener('keypress', ['$event'])
  // onkeypress(e: KeyboardEvent): any {
  //   console.log(`keypress ${e.key} - ${e.keyCode} - ${e.char} - ${e.charCode} - ${e.code} - ${e.composed} - ${e.detail} - ${e.which} - ${e.returnValue}`);
  // }

  @HostListener('keydown', ['$event'])
  onKeyDown(e: KeyboardEvent): any {
    if (!e.key) {
      return;
    }

    if (
      this.navigationKeys.indexOf(e.key) > -1 || // Allow: navigation keys: backspace, delete, arrows etc.
      ((e.key === 'a' || e.code === 'KeyA') && e.ctrlKey === true) || // Allow: Ctrl+A
      ((e.key === 'c' || e.code === 'KeyC') && e.ctrlKey === true) || // Allow: Ctrl+C
      ((e.key === 'v' || e.code === 'KeyV') && e.ctrlKey === true) || // Allow: Ctrl+V
      ((e.key === 'x' || e.code === 'KeyX') && e.ctrlKey === true) || // Allow: Ctrl+X
      ((e.key === 'a' || e.code === 'KeyA') && e.metaKey === true) || // Allow: Cmd+A (Mac)
      ((e.key === 'c' || e.code === 'KeyC') && e.metaKey === true) || // Allow: Cmd+C (Mac)
      ((e.key === 'v' || e.code === 'KeyV') && e.metaKey === true) || // Allow: Cmd+V (Mac)
      ((e.key === 'x' || e.code === 'KeyX') && e.metaKey === true) // Allow: Cmd+X (Mac)
    ) {
      // let it happen, don't do anything
      return;
    }

    this.processInput(e, e.key);
  }

  @HostListener('input', ['$event'])
  onInput(e: InputEvent): any {
    if (!this.isInputSupported) {
      return;
    }
    this.processInput(e, e.data);
  }

  private processInput(e: any, key: any) {
    let newValue = '';

    if (this.decimal && key === this.decimalSeparator) {
      newValue = this.forecastValue(key);
      if (newValue.split(this.decimalSeparator).length > 2) {
        // has two or more decimal points
        e.preventDefault();
        return;
      } else {
        this.hasDecimalPoint = newValue.indexOf(this.decimalSeparator) > -1;
        return; // Allow: only one decimal point
      }
    }

    // Ensure that it is a number and stop the keypress
    if (e.key === ' ' || isNaN(Number(key))) {
      e.preventDefault();
      return;
    }

    newValue = newValue || this.forecastValue(key);
    // check the input pattern RegExp
    if (this.regex) {
      if (!this.regex.test(newValue)) {
        e.preventDefault();
        return;
      }
    }

    const newNumber = Number(newValue);
    if (newNumber > this.max || newNumber < this.min) {
      e.preventDefault();
    }
  }

  private checkInputSupported(): boolean {
    try {
      // just kill browsers off, that throw an error if they don't know
      // `InputEvent`
      const event = new InputEvent('input', {
        data: 'xyz',
        inputType: 'deleteContentForward'
      });
      let support = false;

      // catch the others
      // https://github.com/chromium/chromium/blob/c029168ba251a240b0ec91fa3b4af4214fbbe9ab/third_party/blink/renderer/core/events/input_event.cc#L78-L82
      const el = this.document.createElement('input');
      el.addEventListener('input', (e: InputEvent) => {
        if (e.inputType === 'deleteContentForward') {
          support = true;
        }
      });

      el.dispatchEvent(event);
      return support;
    } catch {
      return false;
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: any): void {
    let pastedInput: string;
    if (window['clipboardData']) {
      // Browser is IE
      pastedInput = window['clipboardData'].getData('text');
    } else if (event.clipboardData && event.clipboardData.getData) {
      // Other browsers
      pastedInput = event.clipboardData.getData('text/plain');
    }

    this.pasteData(pastedInput);
    event.preventDefault();
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    const textData = event.dataTransfer.getData('text');
    this.inputElement.focus();
    this.pasteData(textData);
    event.preventDefault();
  }

  private pasteData(pastedContent: string): void {
    const sanitizedContent = this.sanitizeInput(pastedContent);
    const pasted = this.document.execCommand('insertText', false, sanitizedContent);
    if (!pasted) {
      if (this.inputElement.setRangeText) {
        const { selectionStart: start, selectionEnd: end } = this.inputElement;
        this.inputElement.setRangeText(sanitizedContent, start, end, 'end');
        // Angular's Reactive Form relies on "input" event, but on Firefox, the setRangeText method doesn't trigger it
        // so we have to trigger it ourself.
        if (typeof window['InstallTrigger'] !== 'undefined') {
          this.inputElement.dispatchEvent(new Event('input', { cancelable: true }));
        }
      } else {
        // Browser does not support setRangeText, e.g. IE
        this.insertAtCursor(this.inputElement, sanitizedContent);
      }
    }
    if (this.decimal) {
      this.hasDecimalPoint =
        this.inputElement.value.indexOf(this.decimalSeparator) > -1;
    }
  }

  // The following 2 methods were added from the below article for browsers that do not support setRangeText
  // https://stackoverflow.com/questions/11076975/how-to-insert-text-into-the-textarea-at-the-current-cursor-position
  private insertAtCursor(myField: HTMLInputElement, myValue: string): void {
    const startPos = myField.selectionStart;
    const endPos = myField.selectionEnd;

    myField.value =
      myField.value.substring(0, startPos) +
      myValue +
      myField.value.substring(endPos, myField.value.length);

    const pos = startPos + myValue.length;
    myField.focus();
    myField.setSelectionRange(pos, pos);

    this.triggerEvent(myField, 'input');
  }

  private triggerEvent(el: HTMLInputElement, type: string): void {
    if ('createEvent' in document) {
      // modern browsers, IE9+
      const e = this.document.createEvent('HTMLEvents');
      e.initEvent(type, false, true);
      el.dispatchEvent(e);
    }
  }

  // end stack overflow code

  private sanitizeInput(input: string): string {
    let result = '';
    if (this.decimal && this.isValidDecimal(input)) {
      const regex = new RegExp(`[^0-9${this.decimalSeparator}]`, 'g');
      result = input.replace(regex, '');
    } else {
      result = input.replace(/[^0-9]/g, '');
    }

    const maxLength = this.inputElement.maxLength;
    if (maxLength > 0) {
      // the input element has maxLength limit
      const allowedLength = maxLength - this.inputElement.value.length;
      result = allowedLength > 0 ? result.substring(0, allowedLength) : '';
    }
    return result;
  }

  private isValidDecimal(string: string): boolean {
    if (!this.hasDecimalPoint) {
      return string.split(this.decimalSeparator).length <= 2;
    } else {
      // the input element already has a decimal separator
      const selectedText = this.getSelection();
      if (selectedText && selectedText.indexOf(this.decimalSeparator) > -1) {
        return string.split(this.decimalSeparator).length <= 2;
      } else {
        return string.indexOf(this.decimalSeparator) < 0;
      }
    }
  }

  private getSelection(): string {
    return this.inputElement.value.substring(
      this.inputElement.selectionStart,
      this.inputElement.selectionEnd
    );
  }

  private forecastValue(key: string): string {
    const selectionStart = this.inputElement.selectionStart;
    const selectionEnd = this.inputElement.selectionEnd;
    const oldValue = this.inputElement.value;
    const selection = oldValue.substring(selectionStart, selectionEnd);
    return selection
      ? oldValue.replace(selection, key)
      : oldValue.substring(0, selectionStart) +
      key +
      oldValue.substring(selectionStart);
  }
}
